import { z } from 'zod';
import { createCampaignQuestionLoader } from './campaign-question';
import { apiClient } from './client';
import { mapConcurrently } from '@shared/lib/async/map-concurrently';
import { isCategoryEnabled } from '@shared/config/categories';
import {
  packPlay,
  packAnswer,
  endlessSnapshot,
  endlessStart,
  endlessAnswer,
  endlessAbort,
  soloHint,
  playQuestion,
} from './solo-play-contract';

export type SoloContext =
  { mode: 'pack'; packId: string } | { mode: 'endless'; kind?: 'film' | 'series' | 'anime' | 'game' };
const collectionSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  kind: z.string(),
  questionIds: z.array(z.string()),
  accessSource: z.enum(['free', 'purchased', 'premium', 'locked']),
  price: z.number().nonnegative(),
  isFree: z.boolean(),
  imageUrl: z.string().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  authorLogin: z.string().optional(),
  totalRewards: z.object({ exp: z.number().int().nonnegative(), coins: z.number().int().nonnegative() }).optional(),
});
export type CollectionPack = z.infer<typeof collectionSchema>;
const loadQuestion = createCampaignQuestionLoader(
  (id) => apiClient.questions.getById(id),
  async (ids) =>
    z
      .object({ data: z.array(z.object({ id: z.string(), name: z.string() })) })
      .parse(await apiClient.titles.getByIds({ ids })),
);

export const soloPlayApi = {
  async collection(id: string) {
    const pack = collectionSchema.parse(await apiClient.packs.getById(id));
    if (pack.id !== id) throw new Error('Collection ID mismatch');
    return pack;
  },
  async collections(excludedIds: readonly string[] = [], enabledCategories?: readonly string[]) {
    // The catalogue returns summaries; composition is available in pack details.
    const summaries = z.array(collectionSchema.omit({ questionIds: true })).parse(await apiClient.packs.list());
    return mapConcurrently(
      summaries.filter(
        (pack) =>
          !excludedIds.includes(pack.id) && (!enabledCategories || isCategoryEnabled(pack.kind, enabledCategories)),
      ),
      6,
      async (pack) => {
        return soloPlayApi.collection(pack.id);
      },
    );
  },

  packState: async (id: string) => packPlay.parse(await apiClient.packs.getPlay(id)),
  packStart: async (id: string) => packPlay.parse(await apiClient.packs.startPlay(id)),
  packRestart: async (id: string, actionId: string) =>
    packPlay.parse(await apiClient.packs.restartPlay(id, { idempotencyKey: actionId })),
  packAnswer: async (id: string, questionId: string, titleId: string | undefined, actionId: string) =>
    packAnswer.parse(
      await apiClient.packs.answerPlay(
        id,
        { questionId, titleId, timeout: titleId === undefined, actionId },
        { idempotencyKey: actionId },
      ),
    ),
  endlessState: async () => endlessSnapshot.parse(await apiClient.endless.getSnapshot()),
  endlessStart: async (kind?: string) => endlessStart.parse(await apiClient.endless.start({ kind })),
  endlessAbort: async (actionId: string) =>
    endlessAbort.parse(await apiClient.endless.abort({ actionId }, { idempotencyKey: actionId })),
  endlessContinue: async () => endlessStart.parse(await apiClient.endless.continue()),
  endlessAnswer: async (titleId: string | undefined, actionId: string) =>
    endlessAnswer.parse(
      await apiClient.endless.answer(
        { titleId, timeout: titleId === undefined, actionId },
        { idempotencyKey: actionId },
      ),
    ),
  async hint(
    context: SoloContext,
    questionId: string,
    hint: 'fifty-fifty' | 'freeze' | 'swap-frame',
    actionId: string,
  ) {
    return soloHint.parse(
      await (context.mode === 'pack'
        ? apiClient.packs.usePlayHint(context.packId, { questionId, hint, actionId }, { idempotencyKey: actionId })
        : apiClient.endless.useHint({ hint, actionId }, { idempotencyKey: actionId })),
    );
  },
  async question(value: unknown) {
    const question = playQuestion.parse(typeof value === 'string' ? await apiClient.questions.getById(value) : value);
    const result = await loadQuestion(question.id, question.kind, question);
    return { ...question, options: result.options };
  },
};
